import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { TONE_LABEL, TONE_VAR } from '../lib/status.ts';
import type { StatusTone, Tone } from '../lib/status.ts';
import { Tooltip } from './Tooltip.tsx';

const HUES = ['var(--hue-1)', 'var(--hue-2)', 'var(--hue-3)', 'var(--hue-4)', 'var(--hue-5)', 'var(--hue-6)', 'var(--hue-7)', 'var(--hue-8)'];

/** A stable hue for a name or id. */
export function hueOf(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return HUES[Math.abs(h) % HUES.length] ?? HUES[0]!;
}

/** Up to two initials from a name: "Fix login race" gives "FL". */
export function initialsOf(name: string): string {
  const words = name.replace(/[^\p{L}\p{N}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  const first = words[0] ?? '';
  const second = words[1];
  return (second ? first[0]! + second[0]! : first.slice(0, 2)).toUpperCase();
}

export interface AvatarProps {
  /** Used for initials, colour and the accessible label. */
  name: string;
  /** Overrides the colour key (defaults to name). */
  colorKey?: string;
  /** An icon instead of initials. */
  icon?: ReactNode;
  size?: number;
  square?: boolean;
  className?: string;
  /** Inside a control whose label already names the person or thing: hidden from assistive tech. */
  decorative?: boolean;
}

/**
 * The initials' colour: the hue pulled toward the theme's text colour, so it reads on the
 * hue's own tint in every theme (the raw hue is under 3:1 on its tint in the light theme).
 */
export function avatarInk(hue: string): string {
  return `color-mix(in oklab, ${hue} 45%, var(--fg))`;
}

export function Avatar({ name, colorKey, icon, size = 24, square, className, decorative }: AvatarProps) {
  const hue = hueOf(colorKey ?? name);
  return (
    <span
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': name })}
      className={cn('inline-flex aspect-square shrink-0 select-none items-center justify-center overflow-hidden leading-none font-semibold', square ? 'rounded-control' : 'rounded-pill', className)}
      style={{
        width: size,
        height: size,
        minWidth: size,
        // Never under the 12 px type floor (typography.md); larger avatars scale with their size.
        fontSize: Math.max(12, Math.round(size * 0.4)),
        color: avatarInk(hue),
        background: `color-mix(in oklab, ${hue} 18%, transparent)`,
      }}
    >
      {/* Two initials need 24 px to sit inside the circle at the 12 px floor; smaller avatars show one. */}
      {icon ?? (size < 24 ? initialsOf(name).slice(0, 1) : initialsOf(name))}
    </span>
  );
}

export interface StatusRingProps extends AvatarProps {
  tone: Tone;
  /** Tooltip; defaults to "<name>: <status>". Pass '' for none. */
  tooltip?: ReactNode;
}

/** An avatar inside a static status-coloured ring (never a pulse). Used for sessions on project cards. */
export function StatusRing({ tone, tooltip, size = 24, ...avatar }: StatusRingProps) {
  const color = TONE_VAR[tone];
  const statusText = tone in TONE_LABEL ? TONE_LABEL[tone as StatusTone] : '';
  const ring = (
    <span
      tabIndex={0}
      role="img"
      aria-label={`${avatar.name}${statusText ? `: ${statusText}` : ''}`}
      className="relative inline-flex shrink-0 rounded-full"
      style={{ padding: 2, boxShadow: `inset 0 0 0 1.5px ${color}` }}
    >
      <Avatar size={size} {...avatar} />
    </span>
  );
  const tip = tooltip ?? `${avatar.name}${statusText ? `: ${statusText}` : ''}`;
  return tip ? <Tooltip content={tip}>{ring}</Tooltip> : ring;
}
